import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@vimaana/ui";

import type { Notiz, NotizBezugTeil, NotizNeu, NotizStatus, NotizWer, Person } from "../lib/notizen";

type Liste = { notizen: Notiz[] };

/** Jede Änderung macht alle Notizlisten frisch — Aufgabenseite, Kunde, Projekt, Termin zeigen dieselben Notizen. */
function neuLaden(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["notizen"] });
}

function query(status: NotizStatus, wer: NotizWer, filter: NotizBezugTeil): string {
  const p = new URLSearchParams({ status, wer });
  for (const [k, v] of Object.entries(filter)) if (v) p.set(k, v);
  return p.toString();
}

/**
 * Notizen; `wer` filtert nach Zuständigkeit („meine", „alle", Konto-ID),
 * `filter` auf einen Bezug (z. B. `{ kunde_id }`).
 */
export function useNotizen(status: NotizStatus = "offen", wer: NotizWer = "alle", filter: NotizBezugTeil = {}, enabled = true) {
  const q = query(status, wer, filter);
  return useQuery<Notiz[]>({
    queryKey: ["notizen", q],
    queryFn: async () => (await apiGet<Liste>(`/api/kontor/notizen?${q}`)).notizen,
    enabled,
    staleTime: 20_000,
  });
}

/** Wem sich Aufgaben zuweisen lassen (die Kontor-Konten). */
export function usePersonen() {
  return useQuery<Person[]>({
    queryKey: ["notizen", "personen"],
    queryFn: async () => (await apiGet<{ personen: Person[] }>("/api/kontor/notizen/personen")).personen,
    staleTime: 10 * 60_000,
  });
}

export function useNotizAnzahl() {
  return useQuery<{ notizen: number; punkte: number; zurueckgestellt?: number }>({
    queryKey: ["notizen", "anzahl"],
    queryFn: () => apiGet("/api/kontor/notizen/anzahl"),
    staleTime: 60_000,
  });
}

export function useNotizAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotizNeu) => apiPost<Notiz>("/api/kontor/notizen", body),
    onSuccess: () => neuLaden(qc),
  });
}

export function useNotizAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; text?: string; bezug?: NotizBezugTeil; abgelegt?: boolean; zustaendig_id?: number; wichtig?: boolean }) =>
      apiPatch<Notiz>(`/api/kontor/notizen/${v.id}`, {
        text: v.text, bezug: v.bezug, abgelegt: v.abgelegt, zustaendig_id: v.zustaendig_id, wichtig: v.wichtig,
      }),
    onSuccess: () => neuLaden(qc),
  });
}

/** Aufgabe bis `bis` (YYYY-MM-DD, gilt ab 6 Uhr) ausblenden; `null` holt sie sofort zurück. */
export function useNotizZurueckstellen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; bis: string | null }) =>
      apiPost<Notiz>(`/api/kontor/notizen/${v.id}/zurueckstellen`, { bis: v.bis }),
    onSuccess: () => neuLaden(qc),
  });
}

export function useNotizLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`/api/kontor/notizen/${id}`),
    onSuccess: () => neuLaden(qc),
  });
}

/** Notiz (und ihren Termin ohne Projekt) an ein Projekt hängen — nach „neues Projekt" im Angebot. */
export function useNotizProjekt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; projektId: string }) =>
      apiPost<{ notiz: Notiz; termin_verknuepft: boolean }>(`/api/kontor/notizen/${v.id}/projekt`, { projekt_id: v.projektId }),
    onSuccess: () => {
      neuLaden(qc);
      qc.invalidateQueries({ queryKey: ["kalender"] });
    },
  });
}

export function usePunktAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { notizId: string; text: string }) =>
      apiPost<Notiz>(`/api/kontor/notizen/${v.notizId}/punkte`, { text: v.text }),
    onSuccess: () => neuLaden(qc),
  });
}

export function usePunktAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; text?: string; erledigt?: boolean }) =>
      apiPatch<Notiz>(`/api/kontor/notizen/punkte/${v.id}`, { text: v.text, erledigt: v.erledigt }),
    onSuccess: () => neuLaden(qc),
  });
}

export function usePunktLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<Notiz>(`/api/kontor/notizen/punkte/${id}`),
    onSuccess: () => neuLaden(qc),
  });
}
